import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery } from "@tanstack/react-query";
import { getFirstBookingFunnel } from "./opsApi";

const SECONDS_PER_DAY = 86_400;
const FUNNEL_STALE_MS = 5 * 60_000;

const statStyle = css({ display: "flex", flexDirection: "column", gap: "0.5" });
const labelStyle = css({ fontSize: "xs", color: "fg.muted" });
const valueStyle = css({ fontSize: "lg", fontWeight: "semibold" });

/**
 * The first-booking funnel for operators: did venues get a real online booking within 7 days of going
 * live, and how long did it take? Definitions: docs/business/metrics.md.
 */
export function FirstBookingFunnelPanel() {
  const { t, i18n } = useTranslation();
  const funnel = useQuery({
    queryKey: ["ops", "first-booking-funnel"],
    queryFn: getFirstBookingFunnel,
    // Two cross-tenant booking scans on the ops landing page: these numbers move slowly anyway.
    staleTime: FUNNEL_STALE_MS,
    refetchOnWindowFocus: false,
  });

  const days = (seconds: number | null) =>
    seconds === null
      ? "—"
      : t("ops.funnel.days", {
          value: new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(
            seconds / SECONDS_PER_DAY,
          ),
        });

  const data = funnel.data;
  return (
    <section
      aria-label={t("ops.funnel.title")}
      className={css({
        display: "flex",
        flexDirection: "column",
        gap: "3",
        p: "4",
        mb: "5",
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "md",
      })}
    >
      <h2 className={css({ fontSize: "md", fontWeight: "semibold" })}>{t("ops.funnel.title")}</h2>
      {funnel.isLoading && <p>{t("ops.search.loading")}</p>}
      {funnel.isError && <p className={css({ color: "fg.error" })}>{t("ops.funnel.error")}</p>}
      {data && (
        <>
          <dl
            className={css({
              display: "grid",
              gridTemplateColumns: { base: "1fr", md: "repeat(3, 1fr)" },
              gap: "4",
            })}
          >
            <div className={statStyle}>
              <dt className={labelStyle}>{t("ops.funnel.share")}</dt>
              <dd className={valueStyle}>
                {data.convertedShare === null
                  ? "—"
                  : new Intl.NumberFormat(i18n.language, {
                      style: "percent",
                      maximumFractionDigits: 0,
                    }).format(data.convertedShare)}
              </dd>
              <dd className={labelStyle}>
                {t("ops.funnel.cohort", {
                  converted: data.convertedVenues,
                  cohort: data.cohortVenues,
                })}
              </dd>
            </div>
            <div className={statStyle}>
              <dt className={labelStyle}>{t("ops.funnel.medianSignup")}</dt>
              <dd className={valueStyle}>{days(data.medianSignupToFirstBookingSeconds)}</dd>
            </div>
            <div className={statStyle}>
              <dt className={labelStyle}>{t("ops.funnel.medianLive")}</dt>
              <dd className={valueStyle}>{days(data.medianLiveToFirstBookingSeconds)}</dd>
            </div>
          </dl>
          <div className={statStyle}>
            <h3 className={labelStyle}>{t("ops.funnel.byChannel")}</h3>
            {Object.keys(data.firstBookingsByChannel).length === 0 ? (
              <p className={css({ fontSize: "sm" })}>—</p>
            ) : (
              <ul className={css({ display: "flex", flexWrap: "wrap", gap: "3", fontSize: "sm" })}>
                {Object.entries(data.firstBookingsByChannel).map(([channel, count]) => (
                  <li key={channel}>
                    {t(`bookings.channel.${channel}`)} : <strong>{count}</strong>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className={labelStyle}>{t("ops.funnel.note")}</p>
        </>
      )}
    </section>
  );
}
